import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { ArrowLeft, CreditCard, Palette, Truck, Shield, ChevronRight, Loader2, FileImage } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import TemplateCardPreview from '../components/TemplateCardPreview';
import ShopSidebar from '../components/ShopSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShopSubcategory = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [subcategory,    setSubcategory]    = useState(null);
  const [templates,      setTemplates]      = useState([]);
  const [allCategories,  setAllCategories]  = useState([]);
  const [globalPricing,  setGlobalPricing]  = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);

  useEffect(() => {
    fetchSubcategoryData();
  }, [categorySlug, subcategorySlug]);

  const fetchSubcategoryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [response, templatesResponse, allCatsResponse, pricingResponse] = await Promise.all([
        fetch(`${API_URL}/api/card-designer/subcategories/by-slug/${categorySlug}/${subcategorySlug}`),
        fetch(`${API_URL}/api/card-designer/templates?category_slug=${categorySlug}&subcategory_slug=${subcategorySlug}`),
        fetch(`${API_URL}/api/card-designer/categories`),
        fetch(`${API_URL}/api/shop/global-pricing`),
      ]);
      if (!response.ok) {
        if (response.status === 404) { setError('Subcategory not found'); return; }
        throw new Error('Failed to fetch subcategory');
      }
      setSubcategory(await response.json());
      if (templatesResponse.ok) setTemplates(await templatesResponse.json());
      if (allCatsResponse.ok)   setAllCategories(await allCatsResponse.json());
      if (pricingResponse.ok)   setGlobalPricing(await pricingResponse.json());
    } catch (err) {
      console.error('Error fetching subcategory data:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to load subcategory",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_URL}${imageUrl}`;
  };

  // Strip HTML tags from description
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  const handleDesign = (templateId) => {
    navigate(`/customize/${templateId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  if (error || !subcategory) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Subcategory Not Found</h1>
          <p className="text-gray-600 mb-6">The subcategory you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/shop')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Shop
          </Button>
        </div>
      </Layout>
    );
  }

  const category = subcategory.category;

  return (
    <Layout>
      <Helmet>
        <title>{subcategory.name} - {category?.name} NFC Cards | Piper Card</title>
        <meta name="description" content={subcategory.description || `Browse ${subcategory.name} NFC business cards in ${category?.name}. Premium quality, customizable designs.`} />
        <link rel="canonical" href={`https://pipercard.com/shop/${categorySlug}/${subcategorySlug}`} />
      </Helmet>

      <div className="min-h-screen bg-neutral-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/shop" className="hover:text-amber-600">Shop</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to={`/shop/${categorySlug}`} className="hover:text-amber-600">{category?.name}</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-800 font-medium">{subcategory.name}</span>
          </div>

          <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{subcategory.name}</h1>
              {subcategory.description && <p className="text-gray-500 mt-1 text-sm">{subcategory.description}</p>}
              <p className="text-xs text-gray-400 mt-1">{templates.length} design{templates.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 flex-shrink-0">
              <Truck className="w-4 h-4 text-green-600" /><span>Free Shipping</span>
            </div>
          </div>

          {/* 2-col layout */}
          <div className="flex gap-6">
            {/* Main */}
            <div className="flex-1 min-w-0">
              {/* Global NFC Price Banner */}
              {globalPricing && (
                <div className="mb-5 p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl border border-violet-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                          <path d="M8 12a4 4 0 0 1 8 0"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">All NFC Cards - One Price</p>
                        <p className="text-xs text-gray-500">Choose any design, same price</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                        <p className="text-xs text-gray-400 line-through">₹{globalPricing.nfc_mrp}</p>
                      )}
                      <p className="text-xl font-bold text-violet-600">₹{globalPricing.nfc_price}</p>
                      {globalPricing.nfc_mrp > globalPricing.nfc_price && (
                        <p className="text-[10px] text-emerald-600 font-semibold">
                          {Math.round(((globalPricing.nfc_mrp - globalPricing.nfc_price) / globalPricing.nfc_mrp) * 100)}% OFF
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {templates.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                  <CreditCard className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">No templates available yet.</p>
                  <Button variant="outline" onClick={() => navigate(`/shop/${categorySlug}`)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Browse {category?.name}
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {templates.map(template => (
                    <div key={template.id}
                      onClick={() => { window.scrollTo(0,0); navigate(`/shop/${categorySlug}/${template.slug || template.id}`); }}
                      className="group cursor-pointer bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                    >
                      <div className="relative">
                        {template.thumbnail_image ? (
                          <div className="aspect-[3/4] overflow-hidden">
                            <img src={template.thumbnail_image} alt={template.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          </div>
                        ) : (
                          <div className="aspect-[3/4] bg-gray-50 flex items-center justify-center p-1">
                            <TemplateCardPreview template={template} />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button size="sm" className="bg-white text-gray-900 text-xs">
                            <Palette className="w-3.5 h-3.5 mr-1" /> Customize
                          </Button>
                        </div>
                      </div>
                      <div className="p-2.5">
                        <p className="font-semibold text-gray-900 text-xs line-clamp-1">{template.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-8 flex flex-wrap gap-4 text-xs text-gray-400">
                <div className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-green-500" /> Secure</div>
                <div className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-blue-500" /> Free Shipping</div>
                <div className="flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5 text-amber-500" /> Premium</div>
              </div>

              {/* NFC Smart Cards Info */}
              {globalPricing && (
                <div className="mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                        <path d="M8 12a4 4 0 0 1 8 0"/>
                        <path d="M6 12a6 6 0 0 1 12 0"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">NFC Smart Business Cards</h3>
                      <p className="text-xs text-gray-600">Tap & Share Your Digital Profile Instantly</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-600">All NFC Cards - One Price</p>
                      <p className="text-xs text-gray-500">Premium quality, same great price</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400 line-through">₹{globalPricing.nfc_mrp}</p>
                      <p className="text-xl font-bold text-blue-600">₹{globalPricing.nfc_price}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Tap to Share</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Works with all phones</span>
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">No app needed</span>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="hidden lg:block w-52 flex-shrink-0">
              <div className="sticky top-4">
                <ShopSidebar categories={allCategories} currentSlug={categorySlug} title="Other Categories" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ShopSubcategory;
